import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SectionBadge, IconEye, Highlight, FadeUp } from "@/components/BrandKit";

const IMG = {
  world: "https://static.prod-images.emergentagent.com/jobs/ed48f31c-a867-447d-b67a-34dcc2630b0c/images/62c0a17e5b3e275ee208d0fa98dbc6bae55e30ea7ed7bd3d782109b24c03c3c9.png",
  season: "https://static.prod-images.emergentagent.com/jobs/ed48f31c-a867-447d-b67a-34dcc2630b0c/images/13bb441d94502af6a8a483039d5575885bb8afb8802ca1d78f8c307ca0adbb0f.png",
  cultural: "https://static.prod-images.emergentagent.com/jobs/ed48f31c-a867-447d-b67a-34dcc2630b0c/images/6456d8e5598d61035d55872cb229be92df86f9411fda271840eb8108991a2a96.png",
  experimental: "https://static.prod-images.emergentagent.com/jobs/ed48f31c-a867-447d-b67a-34dcc2630b0c/images/787e9719615ae42e8db38db3bbef8763803c164d69e5517cbffdb477168d995e.png",
  brand: "https://static.prod-images.emergentagent.com/jobs/ed48f31c-a867-447d-b67a-34dcc2630b0c/images/91977585508d85a09234a06ebdb8a850937988c7f22995d412abf68928954925.png",
  campaign: "https://static.prod-images.emergentagent.com/jobs/ed48f31c-a867-447d-b67a-34dcc2630b0c/images/7e04f069596f0de378a5c091716c173d4c65b9e2c76a61ef6a0637b9d0fffc8b.png",
  storyboard: "https://static.prod-images.emergentagent.com/jobs/ed48f31c-a867-447d-b67a-34dcc2630b0c/images/62827bc869b7446360d528a334fcca8872ba2d753def6f1116303fabe6e217c0.png",
};

const WORK = [
  { key: "ai-films", title: "AI Films", tag: "AI Native", img: "/brand/work/ai-films.webp", accent: "#7752B8", href: "https://youtube.com/playlist?list=PL4ukBm6TwqPaUMyG6oEspGozSTmD2CnRX&si=oY5t3U-FdcE4rnzX" },
  { key: "live-action", title: "Live Action", tag: "On Set", img: "/brand/work/live-action.webp", accent: "#341F65", href: "https://www.youtube.com/playlist?list=PL4ukBm6TwqPbb2-UuaTjvhHo5NtXMWoiq" },
  { key: "animation", title: "Animation", tag: "Motion", img: "/brand/work/animation.png", accent: "#B79BE0", href: "https://www.youtube.com/playlist?list=PL4ukBm6TwqPY17feAK1lnThJnSAedAPUL" },
  { key: "product-based", title: "Product Based", tag: "Performance", img: "/brand/work/product-based.webp", accent: "#4A2F87", href: "https://www.youtube.com/playlist?list=PL4ukBm6TwqPYFvyd5K1FMcmm3dfgva5Q1" },
];

function WorkCard({ item, index }) {
  return (
    <motion.a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`work-card-${item.key}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (index % 4) * 0.06 }}
      className="bbs-card border border-black bg-white relative overflow-hidden block group cursor-pointer"
    >
      <div className="relative overflow-hidden h-[260px] sm:h-[280px]">
        <motion.img
          src={item.img}
          alt={item.title}
          className="w-full h-full object-cover"
          style={{ filter: "none" }}
          whileHover={{ scale: 1.06 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        />
        <span
          className="absolute top-3 left-3 inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white"
          style={{ background: item.accent }}
        >
          ● {item.tag}
        </span>
        {/* Watch badge appears on hover */}
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="inline-flex items-center gap-2 bg-white text-black px-4 h-11 font-grotesk uppercase tracking-[0.18em] text-[11px] border border-black">
            Watch on YouTube
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
      <div className="border-t border-black flex items-center justify-between px-4 py-3">
        <h3
          className="font-display uppercase tracking-tight text-base sm:text-lg"
          style={{ fontWeight: 800 }}
        >
          {item.title}
        </h3>
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </motion.a>
  );
}

export default function Work() {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="border-b border-black bg-[#F4EFE6]"
    >
      <div className="max-w-[1480px] mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <FadeUp>
          <SectionBadge
            label="Our Work"
            num="01"
            color="#2A64F6"
            icon={<IconEye className="w-4 h-4 text-white" />}
          />
        </FadeUp>
        <FadeUp delay={0.08}>
          <h2
            data-testid="work-heading"
            className="mt-5 mb-14 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
            style={{ fontWeight: 900 }}
          >
            The Work <Highlight color="#2A64F6">Museum</Highlight>.
          </h2>
        </FadeUp>

        <div
          data-testid="work-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
        >
          {WORK.map((w, i) => (
            <WorkCard item={w} key={w.key} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
